import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { baseURL } from "services/API";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { useAgreementTemplates } from "services/hooks/rentals";
import PdfPlaceholderEditor from "./components/PdfPlaceholderEditor";
import PreviewDialog from "./components/PreviewDialog";
import {
	AGREEMENT_TEMPLATE_META,
	LANDLORD_PREVIEW_DEFAULTS,
	TENANT_PREVIEW_DEFAULTS,
} from "./constants";

const PositionEditor = ({ templateType = "landlord" }) => {
	const { templateId } = useParams();
	const location = useLocation();
	const meta = AGREEMENT_TEMPLATE_META[templateType] || AGREEMENT_TEMPLATE_META.landlord;
	const {
		savePlaceholders,
		previewTemplate,
		uploadOverlayAsset,
		deleteOverlayAsset,
		isSubmitting,
		isPreviewing,
		actionError,
		templatesPath,
	} = useAgreementTemplates(templateType);

	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [editorData, setEditorData] = useState(null);
	const [fields, setFields] = useState([]);
	const [staticValues, setStaticValues] = useState({});
	const [assets, setAssets] = useState({});
	const [coordinateOrigin, setCoordinateOrigin] = useState("bottom_left");
	const [selectedKey, setSelectedKey] = useState(null);
	const [message, setMessage] = useState({ type: "", text: "" });
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewResult, setPreviewResult] = useState(null);
	const [previewError, setPreviewError] = useState(null);
	const [dirty, setDirty] = useState(false);
	const welcomeShownRef = useRef(false);

	const previewDefaults =
		templateType === "tenant" ? TENANT_PREVIEW_DEFAULTS : LANDLORD_PREVIEW_DEFAULTS;

	useEffect(() => {
		let cancelled = false;

		const applyEditorData = (data) => {
			const schema = data.placeholder_schema || {};
			const origin = data.coordinate_origin || schema.coordinate_origin || "bottom_left";
			setEditorData(data);
			setCoordinateOrigin(origin);
			const initialFields = Array.isArray(data.fields)
				? data.fields.map((f) => ({ ...f }))
				: Array.isArray(schema.fields)
				? schema.fields.map((f) => ({ ...f }))
				: [];
			setFields(initialFields);
			setStaticValues({
				...(schema.static_values || {}),
				...(data.static_values || {}),
			});
			setAssets(data.assets || {});
			setDirty(false);
			setSelectedKey(null);
		};

		const load = async () => {
			setLoading(true);
			setLoadError(null);
			try {
				const response = await axios.get(
					`${baseURL}${templatesPath}/${templateId}/placeholders`
				);
				if (cancelled) return;
				applyEditorData(response.data?.data ?? response.data);
			} catch (error) {
				if (cancelled) return;
				const data = error?.response?.data;
				const msg =
					(typeof data === "object" && (data?.message || data?.error)) ||
					error?.message ||
					"Failed to load placeholder editor.";
				setLoadError(msg);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [templateId, templatesPath]);

	useEffect(() => {
		if (location.state?.justCreated && !welcomeShownRef.current) {
			welcomeShownRef.current = true;
			setMessage({
				type: "success",
				text: `Template "${location.state.title || ""}" uploaded. Drag placeholders into position, then save.`,
			});
		}
	}, [location.state?.justCreated, location.state?.title]);

	const unplacedKeys = useMemo(() => {
		if (editorData?.unplaced_keys) {
			return editorData.unplaced_keys.filter((k) => !fields.some((f) => f.key === k));
		}
		const placed = new Set(fields.map((f) => f.key));
		return (editorData?.available_keys || [])
			.map((k) => (typeof k === "string" ? k : k.key))
			.filter((k) => k && !placed.has(k));
	}, [editorData, fields]);

	const handleFieldsChange = useCallback((nextFields) => {
		setFields(nextFields);
		setDirty(true);
	}, []);

	const handleStaticValueChange = useCallback((key, value) => {
		setStaticValues((prev) => ({ ...prev, [key]: value }));
		setDirty(true);
	}, []);

	const buildSavePayload = () => ({
		coordinate_origin: coordinateOrigin,
		fields,
		static_values: staticValues,
	});

	const handleSave = async () => {
		const result = await savePlaceholders(templateId, buildSavePayload());
		if (result.success) {
			setDirty(false);
			setMessage({ type: "success", text: "Placeholder positions saved." });
			const data = result.data;
			if (data?.fields) setFields(data.fields.map((f) => ({ ...f })));
			if (data?.static_values) setStaticValues({ ...data.static_values });
			if (data?.assets) setAssets(data.assets);
			if (data?.placeholder_schema?.coordinate_origin) {
				setCoordinateOrigin(data.placeholder_schema.coordinate_origin);
			}
			setEditorData((prev) =>
				prev
					? {
							...prev,
							fields: data?.fields ?? fields,
							static_values: data?.static_values ?? staticValues,
							assets: data?.assets ?? assets,
							unplaced_keys: data?.unplaced_keys ?? prev.unplaced_keys,
					  }
					: prev
			);
		} else {
			setMessage({ type: "error", text: result.message || "Save failed." });
		}
	};

	const handleUploadSignature = async (key, file) => {
		const result = await uploadOverlayAsset(templateId, key, file);
		if (result.success) {
			if (result.data?.assets) setAssets(result.data.assets);
			setMessage({ type: "success", text: `Uploaded image for ${key}.` });
		} else {
			setMessage({ type: "error", text: result.message || "Upload failed." });
		}
	};

	const handleDeleteSignature = async (key) => {
		const result = await deleteOverlayAsset(templateId, key);
		if (result.success) {
			if (result.data?.assets) setAssets(result.data.assets);
			else {
				setAssets((prev) => {
					const next = { ...prev };
					delete next[key];
					return next;
				});
			}
			setMessage({ type: "success", text: `Removed image for ${key}.` });
		} else {
			setMessage({ type: "error", text: result.message || "Remove failed." });
		}
	};

	const handlePreview = async (payload) => {
		setPreviewError(null);
		if (dirty) {
			const saveFirst = await savePlaceholders(templateId, buildSavePayload());
			if (!saveFirst.success) {
				setPreviewError(saveFirst.message || "Save positions before preview.");
				return;
			}
			setDirty(false);
		}
		const result = await previewTemplate(templateId, payload);
		if (result.success) {
			setPreviewResult(result.data);
		} else {
			setPreviewError(result.message);
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" p={6}>
				<CircularProgress />
			</Box>
		);
	}

	if (loadError) {
		return (
			<Box>
				<Button startIcon={<ArrowBackIcon />} component={RouterLink} to={meta.basePath} sx={{ mb: 2 }}>
					Back
				</Button>
				<Alert severity="error">{loadError}</Alert>
			</Box>
		);
	}

	return (
		<Box>
			<Button startIcon={<ArrowBackIcon />} component={RouterLink} to={meta.basePath} sx={{ mb: 2 }}>
				Back to list
			</Button>

			<Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
				<Box>
					<Typography variant="h4">{editorData?.title || "Position editor"}</Typography>
					<Typography variant="body2" color="text.secondary">
						Template #{templateId} — drag variables, set static text / signatures, then save and preview.
					</Typography>
				</Box>
				<Box display="flex" gap={1} flexWrap="wrap">
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Coordinate origin</InputLabel>
						<Select
							label="Coordinate origin"
							value={coordinateOrigin}
							onChange={(e) => {
								setCoordinateOrigin(e.target.value);
								setDirty(true);
							}}
						>
							<MenuItem value="bottom_left">bottom_left</MenuItem>
							<MenuItem value="top_left">top_left</MenuItem>
						</Select>
					</FormControl>
					<Button
						variant="outlined"
						startIcon={<PreviewIcon />}
						onClick={() => {
							setPreviewResult(null);
							setPreviewError(null);
							setPreviewOpen(true);
						}}
					>
						Preview filled PDF
					</Button>
					<Button
						variant="contained"
						startIcon={<SaveIcon />}
						onClick={handleSave}
						disabled={isSubmitting || !dirty}
					>
						{isSubmitting ? "Saving…" : dirty ? "Save positions" : "Saved"}
					</Button>
				</Box>
			</Box>

			{message.text && (
				<Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>
					{message.text}
				</Alert>
			)}
			{actionError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{actionError}
				</Alert>
			)}
			{editorData?.coordinate_origin_help && (
				<Alert severity="info" sx={{ mb: 2 }}>
					{editorData.coordinate_origin_help}
				</Alert>
			)}

			<PdfPlaceholderEditor
				sourcePdfUrl={editorData?.source_pdf_url}
				fields={fields}
				coordinateOrigin={coordinateOrigin}
				availableKeys={editorData?.available_keys || []}
				unplacedKeys={unplacedKeys}
				onFieldsChange={handleFieldsChange}
				onSelectKey={setSelectedKey}
				selectedKey={selectedKey}
				assets={assets}
				staticValues={staticValues}
				onStaticValueChange={handleStaticValueChange}
				onUploadSignature={handleUploadSignature}
				onDeleteSignature={handleDeleteSignature}
				isSubmitting={isSubmitting}
			/>

			<PreviewDialog
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				onPreview={handlePreview}
				isPreviewing={isPreviewing}
				previewResult={previewResult}
				error={previewError}
				defaultPayload={previewDefaults}
				previewDescription={
					templateType === "tenant"
						? "Sample tenant and occupancy data is sent to the API. Resolved variables are printed on the PDF at your saved positions."
						: "Sample landlord data is sent to the API. Resolved variables are printed on the PDF at your saved positions."
				}
			/>
		</Box>
	);
};

export default PositionEditor;
